/**
 * ============================================================================
 * useEvaluationQuery — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for one vendor evaluation scorecard. Server
 *          returns the supplier header, evaluation period, status, the four
 *          axis scores (Quality / Delivery / Audit / Service), the weighted
 *          composite, the classification, and the historical scorecards for
 *          the same supplier.
 * Endpoint: GET /api/v1/vendor-evaluations/{id}
 *
 * NOTE on weights: the weight vector is server-defined per tenant; we read it
 *  from the payload so a tenant can recalibrate without a UI release. The
 *  default is 40/30/20/10 (Quality / Delivery / Audit / Service).
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type EvaluationStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'SIGNED_APPROVED'
  | 'SIGNED_CONDITIONAL'
  | 'SIGNED_DISQUALIFIED';

export type EvaluationClassification =
  | 'APPROVED'
  | 'CONDITIONAL'
  | 'DISQUALIFIED';

export type EvaluationAxis = 'QUALITY' | 'DELIVERY' | 'AUDIT' | 'SERVICE';

export interface AxisScore {
  axis: EvaluationAxis;
  score: number | null;
  /** Tenant-configured weight, 0..1; sums to 1.0 across the four axes. */
  weight: number;
}

export interface HistoricalScorecard {
  evaluation_id: string;
  period_start_utc: string;
  period_end_utc: string;
  weighted_score: number | null;
  classification: EvaluationClassification | null;
  status: EvaluationStatus;
}

export interface VendorEvaluation {
  evaluation_id: string;
  supplier_id: string;
  supplier_name: string;
  period_start_utc: string;
  period_end_utc: string;
  status: EvaluationStatus;
  axis_scores: AxisScore[];
  weighted_score: number | null;
  classification: EvaluationClassification | null;
  rationale: string | null;
  signers: Array<{ name: string; signed_at_utc: string; meaning: string }>;
  history: HistoricalScorecard[];
  /** Per-tenant policy extras. */
  payload: Record<string, unknown>;
}

export function useEvaluationQuery(
  evaluationId: string,
): UseQueryResult<VendorEvaluation> {
  return useQuery<VendorEvaluation>({
    queryKey: ['vendor-evaluations', evaluationId],
    enabled: !!evaluationId,
    queryFn: ({ signal }) =>
      apiFetch<VendorEvaluation>(
        `/api/v1/vendor-evaluations/${encodeURIComponent(evaluationId)}`,
        { signal },
      ),
  });
}
